import {AccordionStyle, UiStyle} from "@cm/template-nodes"

// The test iframes use contentDocument.write() on about:blank, which inherits the parent's origin.
// This means the iframe shares localStorage with the platform app (localhost:4200). The web
// component's vendor.js includes a full auth service that reads the token and silo values from
// localStorage and runs its auth flow as x-colormass-client: configurator. The backend's
// authorization rules don't support the configurator client accessing membership fields, so the
// queries fail and the auth service calls logOut(), which removes the token from localStorage —
// logging the parent app out.
//
// This guard hides auth-related localStorage keys from the iframe's JS realm (each iframe has its
// own Storage.prototype), making the web component operate as unauthenticated — the same as when
// embedded on an external site where no platform token exists.
export const localStorageGuard = `<script>
(function() {
    var protectedKeys = ['token', 'cm-auth-silo-system-role', 'cm-auth-silo-organization', 'cm-auth-silo-membership', 'cm-csrf-token'];
    var origGetItem = Storage.prototype.getItem;
    Storage.prototype.getItem = function(key) {
        if (protectedKeys.indexOf(key) >= 0) return null;
        return origGetItem.call(this, key);
    };
})();
</script>`

export const getBasicPage = (webComponentSource: string, parameters: string): string => {
    return `
<!DOCTYPE html>
<html>
    <head>
        ${localStorageGuard}
        <link href="https://fonts.googleapis.com/css?family=Roboto:200,300,400,500,600,700" rel="stylesheet" />
        <script src=${webComponentSource} type="module"></script>
        <style>
            html, body {
                width: 100%;
                height: 100%;
                margin: 0;
                overflow: hidden;
            }
            cm-configurator-main {
                display: block;
                width: 100%;
                height: 100%;
            }
        </style>
    </head>
    <body>
        <cm-configurator-main ${parameters} ></cm-configurator-main>
    </body>
</html>
`
}

//The following page is a 1:1 copy from the sample repository
export const getStylingPage = (
    webComponentSource: string,
    uiStyle: UiStyle,
    accordionStyle: AccordionStyle,
    useCaptions: boolean,
    includePdfButton: boolean,
): string => {
    return `
<!DOCTYPE html>
<html style="height: 100%">
    <head>
        ${localStorageGuard}
        <link href="https://fonts.googleapis.com/css?family=Roboto:200,300,400,500,600,700" rel="stylesheet" />

        <!-- Load the colormass configurator web components -->
        <script src="${webComponentSource}" type="module"></script>

        <title>Configurator Web Component Example</title>
        <meta charset="UTF-8" />
        <style>
            :root {
                /* Available css variables */
                --cm-menu-max-num-cols: 8;
                --cm-menu-icon-size: 50px;
                --cm-menu-background-color: rgba(255, 255, 255, 0);
                --cm-tooltip-font-size: 14px;
                --cm-tooltip-background-color: gray;
                font-family: "Roboto";
            }

            .container {
                display: flex;
                height: 100%;
                overflow: hidden;
            }

            .menu {
                justify-content: center;
                width: 15%;
                background-color: #f4f4f4;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                min-width: 400px;
                --color: #d78a5e;

                align-items: center;
                gap: 15px;

                /* The web components use the shadow dom and cannot be accessed directly. The menu currently exposes
                three parts to style: cm-config-group-title, cm-icons-container and cm-icon-container.
                See part="..." in the browser devtools. */
                ::part(cm-config-group-title) {
                    border-width: 1px;
                    border-style: solid;
                    border-color: rgba(0, 140, 153, 0);
                    background-color: var(--color);
                    padding: 10px;
                    color: white;
                    font-size: 16px;
                }

                ::part(cm-config-group-title):hover {
                    border-width: 1px;
                    border-style: solid;
                    border-color: var(--color);
                    background-color: rgba(255, 255, 255, 1);
                    color: var(--color);
                    cursor: pointer;
                }

                ::part(cm-icons-container) {
                    padding-top: 10px;
                    padding-bottom: 10px;
                }

                ::part(cm-icon-container) {
                    border-width: 1px;
                    border-style: solid;
                    border-color: rgba(0, 140, 153, 0);
                }

                ::part(cm-icon-container):hover {
                    border-width: 1px;
                    border-style: solid;
                    border-color: var(--color);
                }

                ::part(cm-icon-with-caption) {
                    gap: 8px;
                    font-size: 10px;
                }

                ::part(cm-config-menu-icons) {
                    gap: 10px;
                }
            }

            cm-configurator-pdf-download-button::part(cm-button) {
                border-width: 1px;
                border-style: solid;
                border-color: rgba(0, 140, 153, 0);
                background-color: var(--color);
                padding: 10px;
                color: white;
            }

            cm-configurator-pdf-download-button::part(cm-button):hover {
                border-color: var(--color);
                background-color: white;
                color: var(--color);
                cursor: pointer;
            }

            .configurator-container {
                flex-grow: 1;
                box-sizing: border-box;
            }
        </style>
    </head>
    <body style="width: 100%; height: 100%; margin: 0">
        <div class="container">
            <div class="menu">
                <!-- Override the default settings for the menu appearance with "accordion"  -->
                <cm-configurator-menu ui-style=${uiStyle} accordion-style=${accordionStyle} ${useCaptions ? "use-captions=true" : ""} style="width: 100%"></cm-configurator-menu>
                ${includePdfButton ? "<cm-configurator-pdf-download-button></cm-configurator-pdf-download-button>" : ""}
            </div>
            <div class="configurator-container">
                <cm-configurator-main template-uuid="22fee4a0-dc34-40c3-a2d3-290126fb8111" use-external-menu="true"></cm-configurator-main>
            </div>
        </div>
    </body>
</html>
`
}

// Reproduces the current Delvaux sample (samples/configurator/delvaux-style-menu):
// a step-through accordion menu with centered group titles, back/forward chevrons
// and 128px captioned icons. The CSS below is the menu-relevant subset of the
// sample's styles.css, copied verbatim. The sample's @font-face blocks are dropped
// (the Atlas Grotesk / Newsreader files aren't reachable from the test iframe), so
// type falls back to the system-ui stack already declared in --cm-font-family.
export const getDelvauxStylePage = (webComponentSource: string): string => {
    return `
<!DOCTYPE html>
<html style="height: 100%">
    <head>
        ${localStorageGuard}
        <link href="https://fonts.googleapis.com/css?family=Roboto:200,300,400,500,600,700" rel="stylesheet" />

        <!-- Load the colormass configurator web components -->
        <script src="${webComponentSource}" type="module"></script>

        <title>Configurator Web Component Example</title>
        <meta charset="UTF-8" />
        <style>
            :root {
                --cm-menu-max-num-cols: 8;
                --cm-menu-icon-size: 50px;
                --cm-menu-gap: 10px;
                /* 16px covers Chrome thin-scrollbar variance across OS themes. */
                --cm-scrollbar-buffer: 16px;
                --cm-menu-background-color: white;
                --cm-tooltip-font-size: 14px;
                --cm-tooltip-background-color: gray;
                /* Picked up by the platform's :host base styles, so the font
                   crosses the shadow boundary into all menu parts. */
                --cm-font-family: system-ui, sans-serif;
                font-family: system-ui, sans-serif;
                /* Theme accent — defined globally so part rules below can use it
                   regardless of which custom element they target. */
                --color: #000000;
                /* Reusable "bold without layout shift" effect. */
                --cm-bold-effect: 0 0 0.7px currentColor, 0 0 0.7px currentColor, 0 0 0.7px currentColor;
            }

            .container {
                display: flex;
                height: 100%;
                overflow: hidden;
            }

            .menu {
                justify-content: center;
                width: 15%;
                background-color: white;
                /* Inner icons-container scrolls; clip anything that spills. */
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                min-width: 460px;
                box-sizing: border-box;
                padding-top: 32px;
            }

            /* Opt the host into the height cascade so the inner icons-container
               can have a bounded scroll region. */
            .menu cm-configurator-menu {
                display: block;
                flex: 1;
                min-height: 0;
            }

            ::part(cm-config-group-title) {
                border: none;
                border-bottom: 1px solid #ddd;
                background-color: transparent;
                padding: 10px;
                color: black;
                font-size: 16px;
                /* Anchor for the absolutely-positioned chevron parts. */
                position: relative;
                /* Center the span; the chevron(s) are taken out of flow so
                   they don't push the text. */
                justify-content: center;
                cursor: pointer;
            }

            ::part(cm-config-group-title-active) {
                text-shadow: var(--cm-bold-effect);
            }

            @media (hover: hover) {
                ::part(cm-config-group-title):hover {
                    text-shadow: var(--cm-bold-effect);
                }
            }

            /* Back/forward chevrons — same FA icon styling, opposite sides.
               Absolute-positioned so the title span stays centered. */
            ::part(cm-config-group-back),
            ::part(cm-config-group-forward) {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                color: #bbb;
                font-size: 12px;
                line-height: 1;
            }
            ::part(cm-config-group-back) {
                left: 10px;
            }
            ::part(cm-config-group-forward) {
                right: 10px;
            }

            /* Step-through: the label itself isn't clickable — only the carets navigate. */
            cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-title) {
                cursor: default;
            }
            cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-back),
            cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-forward) {
                cursor: pointer;
                padding: 16px;
            }
            cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-back) {
                left: 0;
            }
            cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-forward) {
                right: 0;
            }
            @media (hover: hover) {
                cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-title):hover {
                    text-shadow: none;
                }
                cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-back):hover,
                cm-configurator-menu[accordion-style="stepThrough"]::part(cm-config-group-forward):hover {
                    color: black;
                }
            }

            ::part(cm-icons-container),
            ::part(cm-labels-container) {
                padding-top: 10px;
                padding-bottom: 10px;
                flex: 0 1 auto;
                min-height: 0;
                overflow-y: auto;
                scrollbar-gutter: stable;
                /* Stack rows from the top — wrap-flex would otherwise center them. */
                align-content: flex-start;
            }

            ::part(cm-icon-container) {
                border: 1px solid transparent;
                border-radius: 0;
                box-shadow: none;
            }

            @media (hover: hover) {
                ::part(cm-icon-container):hover {
                    border-color: var(--color);
                }
            }

            ::part(cm-icon-with-caption) {
                gap: 8px;
                font-size: 10px;
            }

            ::part(cm-caption) {
                text-align: left;
            }

            cm-configurator-menu[ui-style="accordion"] {
                --cm-menu-icon-size: 128px;
                --cm-menu-max-num-cols: 3;
            }

            /* Scoped to accordion: mobileTabs uses the same part name but a different layout. */
            cm-configurator-menu[ui-style="accordion"]::part(cm-config-menu-icons) {
                gap: 36px;
                padding: 10px;
                box-sizing: border-box;
                max-width: calc(
                    var(--cm-menu-max-num-cols) * var(--cm-menu-icon-size) + (var(--cm-menu-max-num-cols) - 1) * var(--cm-menu-gap) + 20px +
                        var(--cm-scrollbar-buffer)
                );
                justify-content: center;
            }

            /* Override the platform's default max-width: 75vw. */
            cm-configurator-menu[ui-style="mobileTabs"]::part(cm-config-menu-icons) {
                max-width: 100%;
            }

            ::part(cm-mobile-tabs-groups) {
                gap: 10px;
            }

            ::part(cm-scroll-arrow) {
                background: white;
                color: #666;
                border: 1px solid #ccc;
                box-shadow: none;
                width: 28px;
                height: 28px;
            }

            ::part(cm-scroll-arrow):hover {
                background: #555;
                color: white;
                border-color: #555;
            }

            .configurator-container {
                flex-grow: 1;
                box-sizing: border-box;
                min-width: 0;
                min-height: 0;
            }

            cm-configurator-main {
                background-color: white;
            }
        </style>
    </head>
    <body style="width: 100%; height: 100%; margin: 0">
        <div class="container">
            <div class="menu">
                <cm-configurator-menu ui-style="accordion" accordion-style="stepThrough" use-captions="true" style="width: 100%"></cm-configurator-menu>
            </div>
            <div class="configurator-container">
                <cm-configurator-main template-uuid="a92a20c7-434f-41e5-9f70-7e17459f7e27" use-external-menu="true"></cm-configurator-main>
            </div>
        </div>
    </body>
</html>
`
}

export const getPdfButtonPage = (webComponentSource: string): string => {
    return `

    <!DOCTYPE html>
    <html style="height: 100%">
        <head>
            ${localStorageGuard}
            <link href="https://fonts.googleapis.com/css?family=Roboto:200,300,400,500,600,700" rel="stylesheet" />

            <!-- Load the colormass configurator web components -->
            <script src="${webComponentSource}" type="module"></script>

            <title>Configurator Web Component Example</title>
            <meta charset="UTF-8" />
            <style>
                :root {
                    font-family: "Roboto";
                }
    
                .container {
                    display: flex;
                    height: 100%;
                    overflow: hidden;
                }
    
                .menu {
                    justify-content: center;
                    width: 15%;
                    background-color: #f4f4f4;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    min-width: 400px;
                    --color: #d78a5e;
    
                    align-items: center;
                    gap: 15px;
                }
    
                cm-configurator-pdf-download-button::part(cm-button) {
                    border-width: 1px;
                    border-style: solid;
                    border-color: rgba(0, 140, 153, 0);
                    background-color: var(--color);
                    padding: 10px;
                    color: white;
                }
    
                cm-configurator-pdf-download-button::part(cm-button):hover {
                    border-color: var(--color);
                    background-color: white;
                    color: var(--color);
                    cursor: pointer;
                }
    
                .configurator-container {
                    flex-grow: 1;
                    box-sizing: border-box;
                }
            </style>
        </head>
        <body style="width: 100%; height: 100%; margin: 0">
            <div class="container">
                <div class="menu">
                    <cm-configurator-pdf-download-button></cm-configurator-pdf-download-button>
                    <div id="pdf-event-marker">pdfDownloadTriggered: not fired yet</div>
                </div>
                <div class="configurator-container">
                    <cm-configurator-main template-uuid="d12dfd86-a8fb-46d9-81ab-5491866ce126" use-external-menu="true"></cm-configurator-main>
                </div>
            </div>
            <script>
                //shows that the pdfDownloadTriggered event actually reaches the embedding page
                let pdfDownloadCount = 0
                document.querySelector("cm-configurator-main").addEventListener("pdfDownloadTriggered", () => {
                    pdfDownloadCount++
                    document.getElementById("pdf-event-marker").textContent =
                        "pdfDownloadTriggered: " + pdfDownloadCount + "x, last at " + new Date().toLocaleTimeString()
                })
            </script>
        </body>
    </html>
`
}
